// Relative imports (not `@/`) so bun can test this module.

import { getHexTileMapKey } from '../hexmap/model/keys';
import { MAX_COMMAND_UNITS } from '../net/protocol';
import type { RuleSet } from '../rules/schema';
import { unitPlural } from '../rules/ui';
import {
  buildingLevel,
  type Command,
  canPromote,
  canTrain,
  capCount,
  capType,
  type EntityId,
  isMilitary,
  mintOrder,
  type PlayerId,
  promoteCandidates,
  promoteCost,
  type SimContext,
  type SimState,
  territoryOwners,
  trainCost,
  type Unit,
  type UnitType,
  unitCap,
  unitRank,
} from '../sim';
import { ownCities } from './cities';
import {
  costLabel,
  missingLabel,
  promoteRejectionText,
  trainRejectionText,
  unitCityLevel,
} from './reasons';
import { stockInUnits } from './resources';

/**
 * Unit promotions as the HUD offers them (pure, no React): for a selection,
 * one option per type its units can be promoted into ("Upgrade 3 → Rifle
 * Squad"), counting how many of them the stock and cap allow right now.
 */

export interface PromotionOption {
  to: UnitType;
  /** Own selected units with an upgrade into `to`, in selection order. */
  eligible: Unit[];
  /** Those a `promote` command would promote now, in order (stock and cap spent as it goes). */
  ready: Unit[];
  /** What promoting `ready` costs in all; with none ready, all of `eligible`. */
  cost: Record<string, number>;
  /** What one unit costs, when every eligible unit pays the same (null when they differ). */
  each: Record<string, number> | null;
  /** Why the first held-back eligible unit can't be promoted (null when all are ready). */
  reason: string | null;
}

/** The resource-wise sum of `costs`. */
export function sumCosts(
  costs: readonly Readonly<Record<string, number>>[]
): Record<string, number> {
  const total: Record<string, number> = {};
  for (const c of costs)
    for (const [res, n] of Object.entries(c)) total[res] = (total[res] ?? 0) + n;
  return total;
}

export function affordable(stock: Record<string, number>, cost: Record<string, number>): boolean {
  return Object.entries(cost).every(([res, n]) => (stock[res] ?? 0) >= n);
}

/**
 * The promotions `units` (the selection; rivals' are left out) can take, by
 * target type in order of first appearance. As on the city panel, targets
 * of the next era show (locked) and later eras' stay hidden. Each unit is judged by the sim's
 * `canPromote`, then against what the units before it in the same command
 * would already spend and take of the cap, so the count is what one click
 * promotes.
 */
export function promotionOptions(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  units: readonly Unit[],
  real: SimState = state,
  // Once for the whole selection: promotion checks read it per unit.
  owners: ReadonlyMap<string, string> = territoryOwners(state, ctx.rules)
): PromotionOption[] {
  const { rules } = ctx;
  const own = units.filter((u) => u.owner === playerId);
  const topLevel = Math.max(0, ...ownCities(state, rules, playerId).map(buildingLevel));
  const targets: UnitType[] = [];
  for (const u of own) {
    for (const up of rules.units[u.type]?.upgrades ?? []) {
      if (unitCityLevel(rules, up.to) > topLevel + 1) continue;
      if (!targets.includes(up.to)) targets.push(up.to);
    }
  }
  return targets.map((to) => {
    const eligible = own.filter((u) => rules.units[u.type]?.upgrades?.some((up) => up.to === to));
    return judgePromotion(state, ctx, playerId, to, eligible, owners, real);
  });
}

/**
 * One `PromotionOption`: `eligible` (own units with an upgrade into `to`)
 * judged in order by the sim's `canPromote`, then against what the units
 * before them would already spend and take of the cap.
 */
function judgePromotion(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  to: UnitType,
  eligible: Unit[],
  owners: ReadonlyMap<string, string>,
  real: SimState
): PromotionOption {
  const { rules } = ctx;
  const stock = stockInUnits(state, playerId);
  // Caps count the real state: a disguised spy (viewerState.ts) takes no slot.
  let room = unitCap(real, rules, playerId, to) - capCount(real, rules, playerId, to);
  const ready: Unit[] = [];
  const cost: Record<string, number> = {};
  let reason: string | null = null;
  for (const u of eligible) {
    const check = canPromote(state, ctx, u, to, owners);
    if (!check.ok) {
      reason ??= promoteRejectionText(state, rules, u, to, check.reason, owners);
      continue;
    }
    const price = check.upgrade.cost;
    // Promoting within a shared cap (worker → labourer) doesn't use any of it.
    const shared = capType(rules, u.type) === capType(rules, to);
    if (!shared && room <= 0) {
      reason ??= `${rules.units[to]?.name ?? to} limit reached (${unitCap(real, rules, playerId, to)})`;
      continue;
    }
    if (!affordable(stock, price)) {
      reason ??= missingLabel(stock, price) ?? 'Not enough resources';
      continue;
    }
    for (const [res, n] of Object.entries(price)) {
      stock[res] = (stock[res] ?? 0) - n;
      cost[res] = (cost[res] ?? 0) + n;
    }
    if (!shared) room--;
    ready.push(u);
  }
  const prices = eligible.map((u) => promoteCost(rules, u.type, to) ?? {});
  const each = prices.every((p) => costLabel(p) === costLabel(prices[0] ?? {}))
    ? (prices[0] ?? null)
    : null;
  // None ready (the button is disabled): what all of them would cost.
  if (ready.length === 0) return { to, eligible, ready, cost: sumCosts(prices), each, reason };
  return { to, eligible, ready, cost, each, reason };
}

/** The `promote` commands for an option's ready units, split at `MAX_COMMAND_UNITS`. */
export function promoteCommands(option: PromotionOption): Command[] {
  const commands: Command[] = [];
  for (let i = 0; i < option.ready.length; i += MAX_COMMAND_UNITS) {
    const unitIds = option.ready.slice(i, i + MAX_COMMAND_UNITS).map((u) => u.id);
    commands.push({ type: 'promote', unitIds, to: option.to });
  }
  return commands;
}

/** A unit type's name for `n` of them: "Archer", "Archers", "Spies", "Cavalry". */
export function unitName(rules: RuleSet, type: UnitType, n: number): string {
  return n === 1 ? (rules.units[type]?.name ?? type) : unitPlural(rules, type);
}

/** "3 Archers", "1 Archer", "2 Cavalry", "2 Spies". */
export function unitCountText(rules: RuleSet, type: UnitType, n: number): string {
  return `${n} ${unitName(rules, type, n)}`;
}

/**
 * The promote button's label, the same form as the toast it leads to:
 * "Upgrade 4 → Labourers", "Upgrade 4 of 6 → Labourers" (two held back), or
 * for one unit (or none ready) "Upgrade → Labourer".
 */
export function promoteLabel(rules: RuleSet, option: PromotionOption): string {
  const n = option.ready.length;
  const all = option.eligible.length;
  if (all === 1) return `Upgrade → ${unitName(rules, option.to, 1)}`;
  // None ready: all of them, as the (total) cost beside it counts.
  const count = n > 0 && n < all ? `${n} of ${all}` : `${n || all}`;
  return `Upgrade ${count} → ${unitName(rules, option.to, n || all)}`;
}

/**
 * The promote button's tooltip: what the new type keeps, the cost of each
 * and of all ready ones, and why any are held back.
 */
export function promoteTitle(rules: RuleSet, option: PromotionOption): string {
  const toName = rules.units[option.to]?.name ?? option.to;
  const n = option.ready.length;
  const parts = [`${toName}: keeps its rank, group and hp fraction`];
  if (option.each) parts.push(`${costLabel(option.each)} each`);
  const all = option.eligible.length;
  if (n > 1 || (n === 0 && all > 1)) parts.push(`${n || all} for ${costLabel(option.cost)}`);
  const held = all - n;
  if (held > 0 && option.reason) {
    parts.push(n > 0 ? `${held} more can't: ${option.reason}` : option.reason);
  }
  return parts.join(' · ');
}

/** "4 Workers" (one type), "Worker" (one unit), "3 Archers, 1 Warrior" (mixed). */
export function unitsText(rules: RuleSet, types: readonly UnitType[]): string {
  if (types.length === 1) return unitName(rules, types[0], 1);
  const counts = new Map<UnitType, number>();
  for (const t of types) counts.set(t, (counts.get(t) ?? 0) + 1);
  return [...counts].map(([t, n]) => unitCountText(rules, t, n)).join(', ');
}

/**
 * "4 Workers upgraded to Labourers", "Worker upgraded to Labourer",
 * "2 Archers, 1 Warrior upgraded to Gun Squads": units by their type before
 * the promotion.
 */
export function promotedText(rules: RuleSet, fromTypes: readonly UnitType[], to: UnitType): string {
  return `${unitsText(rules, fromTypes)} upgraded to ${unitName(rules, to, fromTypes.length)}`;
}

/** A promotion open in a city, with its units there (CityPanel's Upgrades chips). */
export interface CityPromotion {
  from: UnitType;
  /** The city's units of `from` judged as one `promote … count: 'all'` would take them, best first. */
  option: PromotionOption;
}

/**
 * The promotions open in `city` (pure, no React): every upgrade with no
 * `at`, or whose `at` building stands completed at its level in the city's
 * territory, with the city's units of its `from` type in the order the
 * sim's city-wide `promote` picks them (`promoteCandidates`, best first):
 * `option.ready[0]` is the one a click promotes, `option.ready` what a
 * shift-click (all) promotes.
 */
export function cityPromotions(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  cityId: string,
  real: SimState = state
): CityPromotion[] {
  const { rules } = ctx;
  const owners = territoryOwners(state, rules);
  const atHere = (building: string, level: number) =>
    Object.values(state.buildings).some(
      (b) =>
        b.owner === playerId &&
        b.type === building &&
        b.buildProgress >= 1 &&
        buildingLevel(b) >= level &&
        owners.get(getHexTileMapKey(b.pos)) === cityId
    );
  return Object.entries(rules.units).flatMap(([from, def]) =>
    (def.upgrades ?? [])
      .filter(({ at }) => !at || atHere(at.building, at.level))
      .map((up) => {
        const units = promoteCandidates(state, rules, playerId, cityId, up.to, from, owners);
        return {
          from,
          option: judgePromotion(state, ctx, playerId, up.to, units, owners, real),
        };
      })
  );
}

/**
 * The city-wide `promote` for a chip: one unit (the best, picked by the
 * sim), or with `all` as many as are ready (so the toast that follows
 * reports exactly those, not a refusal for ones the stock never covered).
 */
export function cityPromoteCommand(cityId: string, path: CityPromotion, all: boolean): Command {
  const count = all ? Math.min(MAX_COMMAND_UNITS, Math.max(1, path.option.ready.length)) : 1;
  return {
    type: 'promote',
    cityId,
    to: path.option.to,
    from: path.from,
    ...(count > 1 ? { count } : {}),
  };
}

/** "Archer → Rifle Squad · 20🍞 15🪵 · 4 eligible": a city's Upgrades chip. */
export function cityPromoteLabel(rules: RuleSet, path: CityPromotion): string {
  const { option } = path;
  // No candidates in the city: the definition's price, so it still shows.
  const each = costLabel(option.each ?? promoteCost(rules, path.from, option.to) ?? option.cost);
  return `${unitName(rules, path.from, 1)} → ${unitName(rules, option.to, 1)} · ${each} · ${option.eligible.length} eligible`;
}

/** The chip's tooltip: what a click and a shift-click promote, or why neither can. */
export function cityPromoteTitle(rules: RuleSet, path: CityPromotion): string {
  const { option } = path;
  const n = option.ready.length;
  if (option.eligible.length === 0) {
    const price = costLabel(promoteCost(rules, path.from, option.to) ?? option.cost);
    return `No ${unitName(rules, path.from, 2)} in this city's territory (${price} each)`;
  }
  if (n === 0) return option.reason ?? `Can't upgrade to ${unitName(rules, option.to, 1)}`;
  const one = `Click: upgrade the best ${unitName(rules, path.from, 1)} (highest rank, then most hp)`;
  const all = `shift-click: all ${n} for ${costLabel(option.cost)}`;
  const held = option.eligible.length - n;
  const rest = held > 0 && option.reason ? ` · ${held} more can't: ${option.reason}` : '';
  return `${one} · ${all}${rest}`;
}

/** Rank up: buying the next rank for several units at once (one `train` each). */
export interface TrainOption {
  /** Own military units in the selection that aren't at the top rank. */
  eligible: Unit[];
  /** Those one click trains now, in selection order (stock spent as it goes). */
  ready: Unit[];
  /** What training `ready` costs in all; with none ready, every eligible one's next rank. */
  cost: Record<string, number>;
  /** Why the first held-back eligible unit can't be trained (null when all are ready). */
  reason: string | null;
}

/**
 * How the selection's own military units can be trained a rank now: each
 * judged by the sim's `canTrain`, then against what the units before it
 * would already spend (each pays `trainCost` for its own next rank). Null
 * when the selection has no own military unit below the top rank.
 */
export function trainOption(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  units: readonly Unit[]
): TrainOption | null {
  const eligible = units.filter(
    (u) =>
      u.owner === playerId &&
      isMilitary(rules, u.type) &&
      unitRank(rules, u) < rules.veterancy.ranks.length
  );
  if (eligible.length === 0) return null;
  const stock = stockInUnits(state, playerId);
  const ready: Unit[] = [];
  const cost: Record<string, number> = {};
  let reason: string | null = null;
  for (const u of eligible) {
    const check = canTrain(state, rules, playerId, u.id);
    if (!check.ok && check.reason !== 'cost') {
      reason ??= trainRejectionText(state, rules, u, check.reason);
      continue;
    }
    const price = trainCost(rules, unitRank(rules, u) + 1);
    if (!affordable(stock, price)) {
      reason ??= missingLabel(stock, price) ?? 'Not enough resources';
      continue;
    }
    for (const [res, n] of Object.entries(price)) {
      stock[res] = (stock[res] ?? 0) - n;
      cost[res] = (cost[res] ?? 0) + n;
    }
    ready.push(u);
  }
  // None ready (the button is disabled): what all of them would cost.
  if (ready.length === 0) {
    const all = sumCosts(eligible.map((u) => trainCost(rules, unitRank(rules, u) + 1)));
    return { eligible, ready, cost: all, reason };
  }
  return { eligible, ready, cost, reason };
}

/**
 * The `train` commands for an option's ready units: one for a lone unit,
 * else batches of up to `MAX_COMMAND_UNITS` (the host checks each unit as it
 * comes, so a batch never overspends).
 */
export function trainCommands(option: TrainOption): Command[] {
  if (option.ready.length === 1) return [{ type: 'train', unitId: option.ready[0].id }];
  const commands: Command[] = [];
  for (let i = 0; i < option.ready.length; i += MAX_COMMAND_UNITS) {
    const unitIds = option.ready.slice(i, i + MAX_COMMAND_UNITS).map((u) => u.id);
    commands.push({ type: 'train', unitIds });
  }
  return commands;
}

// ── By unit type, player-wide (the Units panel's per-type actions) ─────────

/**
 * `units` best first: those `valid` (where they stand lets them be upgraded
 * or trained) first, then the highest rank, the most hp, the oldest.
 */
function bestFirst(rules: RuleSet, units: Unit[], valid: (u: Unit) => boolean): Unit[] {
  const ok = new Map(units.map((u) => [u.id, valid(u)]));
  return units.sort(
    (a, b) =>
      Number(ok.get(b.id)) - Number(ok.get(a.id)) ||
      unitRank(rules, b) - unitRank(rules, a) ||
      b.hp - a.hp ||
      mintOrder(a.id) - mintOrder(b.id)
  );
}

/** `playerId`'s units of `type`, in mint order. */
function unitsOfType(state: SimState, playerId: PlayerId, type: UnitType): Unit[] {
  return Object.values(state.units)
    .filter((u) => u.owner === playerId && u.type === type)
    .sort((a, b) => mintOrder(a.id) - mintOrder(b.id));
}

/**
 * Every promotion open to the player's units of `type`, wherever they are
 * (pure, no React): one `PromotionOption` per target type (later eras
 * hidden, as `promotionOptions`), its `eligible` units best first (those
 * standing where they may be promoted, then the highest rank and most hp),
 * `ready` the ones one click upgrades with the stock and cap now (an "upgrade
 * as many as you can afford" button), `cost` their total and `reason` why the
 * rest can't. Send `promoteCommands(option)`: the host re-checks each unit,
 * so it never overspends.
 */
export function typePromotions(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  type: UnitType,
  real: SimState = state,
  owners: ReadonlyMap<string, string> = territoryOwners(state, ctx.rules)
): PromotionOption[] {
  const { rules } = ctx;
  const units = unitsOfType(state, playerId, type);
  if (units.length === 0) return [];
  const topLevel = Math.max(0, ...ownCities(state, rules, playerId).map(buildingLevel));
  return (rules.units[type]?.upgrades ?? [])
    .filter((up) => unitCityLevel(rules, up.to) <= topLevel + 1)
    .map((up) => {
      const where = (u: Unit) => {
        const check = canPromote(state, ctx, u, up.to, owners);
        return check.ok || (check.reason !== 'where' && check.reason !== 'unknown');
      };
      const eligible = bestFirst(rules, [...units], where);
      return judgePromotion(state, ctx, playerId, up.to, eligible, owners, real);
    });
}

/**
 * Ranking up the player's military units of `type` one rank each, wherever
 * they are (pure): a `TrainOption` over every one below the top rank, best
 * first (those at a barracks able to train them, then the highest rank and
 * most hp), `ready` the ones one click trains with the stock now (each pays
 * its own next rank). Null when none is below the top rank. Send
 * `trainCommands(option)`.
 */
export function typeTrainOption(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  type: UnitType
): TrainOption | null {
  const trainable = (u: Unit) => {
    const check = canTrain(state, rules, playerId, u.id);
    return check.ok || check.reason === 'cost';
  };
  return trainOption(
    state,
    rules,
    playerId,
    bestFirst(rules, unitsOfType(state, playerId, type), trainable)
  );
}

/** Unit ids of a promotion's or training's `ready` units (what one click sends). */
export function readyIds(option: { ready: readonly Unit[] }): EntityId[] {
  return option.ready.map((u) => u.id);
}

/** "Rank up to 2" (one unit), "Rank up 5", "Rank up 3 of 5". */
export function trainLabel(rules: RuleSet, option: TrainOption): string {
  const all = option.eligible.length;
  if (all === 1) return `Rank up to ${unitRank(rules, option.eligible[0]) + 1}`;
  const n = option.ready.length;
  const count = n > 0 && n < all ? `${n} of ${all}` : `${n || all}`;
  return `Rank up ${count}`;
}

/** The Rank up button's tooltip: what a rank gives, the cost of all ready ones, and why any are held back. */
export function trainTitle(rules: RuleSet, option: TrainOption): string {
  const { hpPerRank, strengthPerRank } = rules.veterancy;
  const parts = [
    `Each rank: +${Math.round(hpPerRank * 100)}% hp and +${Math.round(strengthPerRank * 100)}% damage or healing`,
    `${costLabel(rules.veterancy.trainCost)} × the rank bought each`,
  ];
  const n = option.ready.length;
  // None ready: what all of them would cost (the button shows the same total).
  const paying = n || option.eligible.length;
  parts.push(`${paying === 1 ? 'Costs' : `${paying} for`} ${costLabel(option.cost)}`);
  const held = option.eligible.length - n;
  if (held > 0 && option.reason) {
    parts.push(n > 0 ? `${held} more can't: ${option.reason}` : option.reason);
  }
  return parts.join(' · ');
}
